import {
  endorsementCoverages,
  fullDataForCode,
  regularCoverages,
} from 'data-layer-cr/dist/functions/coverages';
import {
  coverageForCode,
  getRegularCoveragesForUnit,
} from 'data-layer-cr/dist/functions/quotations';
import {
  CoverageCatalog,
  CoverageCatalogResume,
  QuotationUnit,
} from 'data-layer-cr/dist/models';
import { moneyFormat } from 'design-system';
import { useMemo } from 'react';
import {
  getDeductibleDefaultBase,
  getDeductibleDisplayText,
  getDeductibleHelperText,
} from 'src/functions/coverageMethods';
import {
  getTypeOfQuotation,
  typeOfQuotation,
} from 'src/functions/individualOptionHelpers';

const { formatMoney } = moneyFormat;

export type UseCoverageDataProps = {
  unit: QuotationUnit;
  coverageCatalogs: CoverageCatalog[];
  coverageCatalogResumes: CoverageCatalogResume[];
  moneyType: string | undefined;
  packageOption?: number;
  createdAt?: string;
};

export type CoverageDetail = {
  code: string;
  name: string;
  value: string;
};

export type DeductibleDetail = {
  code: string;
  name: string;
  value: string;
  helperText: string;
  showHelper: boolean;
};

export function useCoverageData({
  unit,
  coverageCatalogResumes,
  coverageCatalogs,
  moneyType,
  packageOption = 1,
  createdAt,
}: UseCoverageDataProps): {
  coveragesResume: CoverageDetail[];
  deductibleResume: DeductibleDetail[];
  quotationEndorsement: CoverageDetail;
} {
  const usedCoverages = useMemo(
    () => getRegularCoveragesForUnit(unit, coverageCatalogResumes),
    [unit, coverageCatalogResumes],
  );

  const coveragesResume = useMemo((): CoverageDetail[] => {
    const resume: CoverageDetail[] = [];
    regularCoverages(coverageCatalogs).forEach((coverage) => {
      const coverageData = coverageForCode(usedCoverages, coverage.code);
      if (coverageData) {
        resume.push({
          code: coverage.code,
          name: coverage.name,
          value: formatMoney(coverageData.insuredValue, false, moneyType),
        });
      }
    });
    return resume;
  }, [coverageCatalogs, usedCoverages, moneyType]);

  const deductibleResume = useMemo(() => {
    const resume: DeductibleDetail[] = [];
    regularCoverages(coverageCatalogs).forEach((coverage) => {
      const coverageData = coverageForCode(usedCoverages, coverage.code);
      if (coverageData) {
        const deductibleType = getDeductibleDefaultBase(
          packageOption,
          coverage.code,
          createdAt,
        );
        const quotationType = getTypeOfQuotation(createdAt);
        let deductibleRate = coverageData.deductible;
        const { packageOptions } = typeOfQuotation[quotationType];
        if (packageOptions?.[packageOption] && coverageData.deductibleRate) {
          deductibleRate = coverageData.deductibleRate;
        }

        const displayText = getDeductibleDisplayText(
          deductibleType,
          deductibleRate,
          coverageData.deductibleRate ?? 0,
          moneyType,
          coverage.code,
          packageOption,
          createdAt,
        );

        const helperText = getDeductibleHelperText(
          deductibleType,
          coverageData.deductible,
          moneyType,
        );

        const showHelper =
          deductibleType === 'P' ? true : Boolean(deductibleRate);
        resume.push({
          code: coverage.code,
          name: coverage.name,
          value: displayText,
          helperText,
          showHelper,
        });
      }
    });
    return resume;
  }, [coverageCatalogs, moneyType, packageOption, usedCoverages]);

  const quotationEndorsement = useMemo((): CoverageDetail => {
    const resume: CoverageDetail = { code: '', name: '', value: '' };
    const endorsementCoverageList = endorsementCoverages(coverageCatalogs);
    unit.coverages.forEach((coverage) => {
      const endorsement = fullDataForCode(
        endorsementCoverageList,
        coverage.coverageId,
      );
      if (endorsement) {
        resume.code = endorsement.code;
        resume.name = endorsement.name;
        resume.value = formatMoney(endorsement.maxCost, false, moneyType);
      }
    });
    return resume;
  }, [coverageCatalogs, unit, moneyType]);

  return { coveragesResume, deductibleResume, quotationEndorsement };
}
